import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Device from '../../../../../devices/device';
import Translations from '../../../../../requests/apa/Translator';
import { sendMetricScroll } from '../../../utils';
import './_MatchDetailBody.scss';

const PLAYERS_SWITCH_BUTTON_IDS = ['match-detail-switch-btn-0', 'match-detail-switch-btn-1'];
const PLAYERS_SWITCH_SCROLL_ACTIVE_CLASS = 'match-detail-players-switch__button--scroll-active';
const SUMMARY_TOGGLE_ID = 'match-detail-summary-toggle';
const SUMMARY_TOGGLE_SCROLL_UNFOCUSED_CLASS = 'match-detail-summary__top-switch--scroll-unfocused';

class MatchDetailBody extends Component {
  constructor(props) {
    super(props);
    const device = Device.getDevice && Device.getDevice();
    this.keys = device && device.getKeys ? device.getKeys() : null;
    this.contentRef = null;
    this.railRef = null;
    this.thumbRef = null;
    this.isThumbExpandedByScroll = false;
    this.scrollMilestonesSent = new Set();
    this.lastScrollTop = 0;
  }

  componentDidMount() {
    this.updateThumb();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateThumb);
      window.addEventListener('keydown', this.captureKeyDown, true);
      window.addEventListener('focusin', this.handleGlobalFocusIn, true);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeTabId !== this.props.activeTabId) {
      this.scrollMilestonesSent = new Set();
      this.lastScrollTop = 0;
    }
    this.updateThumb();
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateThumb);
      window.removeEventListener('keydown', this.captureKeyDown, true);
      window.removeEventListener('focusin', this.handleGlobalFocusIn, true);
    }
  }

  getPressedKey = (e) => {
    const mappedKey = this.keys && this.keys.getPressKey ? this.keys.getPressKey(e.keyCode) : null;
    if (mappedKey) return mappedKey;
    if (e.key === 'ArrowDown' || e.keyCode === 40) return 'DOWN';
    if (e.key === 'ArrowUp' || e.keyCode === 38) return 'UP';
    if (e.key === 'ArrowLeft' || e.keyCode === 37) return 'LEFT';
    if (e.key === 'ArrowRight' || e.keyCode === 39) return 'RIGHT';
    return null;
  };

  setScrollThumbFocused = (isFocused) => {
    const bodyEl = this.contentRef && this.contentRef.parentElement;
    if (!bodyEl) return;
    bodyEl.classList.toggle('match-detail-body--scroll-focused', isFocused);
  };

  setThumbExpandedByScroll = (isExpanded) => {
    this.isThumbExpandedByScroll = isExpanded;
    this.setScrollThumbFocused(isExpanded);
  };

  isButtonLikeTarget = (el) => {
    if (!el || el === this.contentRef) return false;

    const tagName = el.tagName ? el.tagName.toLowerCase() : '';
    if (tagName === 'button' || tagName === 'a') return true;

    const role = el.getAttribute && el.getAttribute('role');
    if (role === 'button') return true;

    const id = el.id || '';
    if (id.indexOf('btn') !== -1 || id.indexOf('button') !== -1) return true;

    const className = typeof el.className === 'string' ? el.className : '';
    if (className.indexOf('btn') !== -1 || className.indexOf('button') !== -1) return true;

    return false;
  };

  isPlayersSwitchButton = (el) => {
    if (!el || !el.id) return false;
    return PLAYERS_SWITCH_BUTTON_IDS.indexOf(el.id) !== -1;
  };

  clearPlayersSwitchScrollStyle = () => {
    PLAYERS_SWITCH_BUTTON_IDS.forEach((id) => {
      const target = document.getElementById(id);
      if (target) target.classList.remove(PLAYERS_SWITCH_SCROLL_ACTIVE_CLASS);
    });
  };

  syncPlayersSwitchScrollStyle = () => {
    const content = this.contentRef;
    if (!content) return;

    const isAtTop = content.scrollTop <= 1;
    if (isAtTop) {
      this.clearPlayersSwitchScrollStyle();
      return;
    }

    this.clearPlayersSwitchScrollStyle();
    const focused = document.activeElement;
    if (this.isPlayersSwitchButton(focused)) {
      focused.classList.add(PLAYERS_SWITCH_SCROLL_ACTIVE_CLASS);
    }
  };

  syncSummaryToggleScrollStyle = () => {
    const content = this.contentRef;
    if (!content) return;

    const toggle = document.getElementById(SUMMARY_TOGGLE_ID);
    if (!toggle) return;

    const isAtTop = content.scrollTop <= 1;
    if (isAtTop) {
      toggle.classList.remove(SUMMARY_TOGGLE_SCROLL_UNFOCUSED_CLASS);
      return;
    }

    const focused = document.activeElement;
    const shouldUseUnfocusedStyle = focused && focused.id === SUMMARY_TOGGLE_ID;
    toggle.classList.toggle(SUMMARY_TOGGLE_SCROLL_UNFOCUSED_CLASS, shouldUseUnfocusedStyle);
  };

  syncScrollDrivenButtonStyles = () => {
    this.syncPlayersSwitchScrollStyle();
    this.syncSummaryToggleScrollStyle();
  };

  getActiveTabMetricLabel = () => {
    const { activeTabId } = this.props;
    const tabId = (activeTabId || '').replace('#match-detail-tab-', '');
    if (tabId === 'stats') return Translations.get('eventodeportivo_partidos_tabs_texto_tab1');
    if (tabId === 'players') return Translations.get('eventodeportivo_partidos_tabs_texto_tab2');
    if (tabId === 'summary') return Translations.get('eventodeportivo_partidos_tabs_texto_tab3');
    if (tabId === 'lineups') return Translations.get('eventodeportivo_partidos_tabs_texto_tab4');
    return null;
  };

  getMetricContext = () => {
    const mainMenuLabel = this.props.mainMenuLabel || Translations.get('eventodeportivo_pestanasecciones_texto_partidos');
    const parentTabLabel = this.props.parentTabLabel || this.getActiveTabMetricLabel();
    const childTabLabel = this.props.childTabLabel || null;
    return { mainMenuLabel, parentTabLabel, childTabLabel };
  };

  trackScrollMilestones = () => {
    const el = this.contentRef;
    if (!el) return;

    const maxScroll = Math.max(el.scrollHeight - el.clientHeight, 0);
    if (maxScroll <= 0) {
      this.lastScrollTop = 0;
      this.scrollMilestonesSent = new Set();
      return;
    }

    const prevScrollTop = this.lastScrollTop;
    const nextScrollTop = el.scrollTop;
    const prevPercent = Math.round((prevScrollTop / maxScroll) * 100);
    const nextPercent = Math.round((nextScrollTop / maxScroll) * 100);
    const isDown = nextScrollTop > prevScrollTop;
    const isUp = nextScrollTop < prevScrollTop;

    if (!isDown && !isUp) {
      this.lastScrollTop = nextScrollTop;
      return;
    }

    const { mainMenuLabel, parentTabLabel, childTabLabel } = this.getMetricContext();

    if (isDown) {
      [25, 50, 75].forEach((milestone) => {
        if (prevPercent < milestone && milestone <= nextPercent && !this.scrollMilestonesSent.has(milestone)) {
          this.scrollMilestonesSent.add(milestone);
          sendMetricScroll(mainMenuLabel, parentTabLabel, childTabLabel, milestone);
        }
      });

      if (nextScrollTop >= maxScroll && !this.scrollMilestonesSent.has(100)) {
        this.scrollMilestonesSent.add(100);
        sendMetricScroll(mainMenuLabel, parentTabLabel, childTabLabel, 100);
      }
    }

    if (isUp) {
      if (this.scrollMilestonesSent.has(100) && nextScrollTop < maxScroll - 1) {
        this.scrollMilestonesSent.delete(100);
      }

      [25, 50, 75].forEach((milestone) => {
        if (nextPercent < milestone && milestone <= prevPercent && this.scrollMilestonesSent.has(milestone)) {
          this.scrollMilestonesSent.delete(milestone);
          sendMetricScroll(mainMenuLabel, parentTabLabel, childTabLabel, milestone);
        }
      });
    }

    this.lastScrollTop = nextScrollTop;
  };

  captureKeyDown = (e) => {
    const active = document.activeElement;
    const currentKey = this.getPressedKey(e);

    const el = this.contentRef;
    if (!el) return;
    const isInsideContent = active === el || (el.contains && el.contains(active));
    if (!isInsideContent) return;

    if (!currentKey || !['UP', 'DOWN'].includes(currentKey)) return;

    const atTop = el.scrollTop <= 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    if (currentKey === 'DOWN') {
      if (!atBottom) {
        e.stopImmediatePropagation();
        e.preventDefault();
        el.scrollTop += 120;
        this.setThumbExpandedByScroll(true);
        this.updateThumb();
      }
      return;
    }

    if (currentKey === 'UP') {
      if (!atTop) {
        e.stopImmediatePropagation();
        e.preventDefault();
        el.scrollTop -= 120;
        this.setThumbExpandedByScroll(true);
        this.updateThumb();
      }
    }
  };

  handleGlobalFocusIn = (e) => {
    if (!this.isButtonLikeTarget(e.target)) return;
    this.setThumbExpandedByScroll(false);
    this.syncScrollDrivenButtonStyles();
  };

  handleKeyDown = (e) => {
    const currentKey = this.getPressedKey(e);
    if (!currentKey || !['UP', 'DOWN', 'LEFT', 'RIGHT'].includes(currentKey)) return;

    const el = this.contentRef;
    if (!el) return;
    const atTop = el.scrollTop <= 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    // Al llegar al final, bloquear navegación lateral/baja para que solo se pueda subir con UP.
    if (atBottom && currentKey !== 'UP') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (currentKey === 'RIGHT' && atTop) {
      e.preventDefault();
      e.stopPropagation();
      var activeTab = document.querySelector('.match-detail-tabs__tab--active');
      if (activeTab && activeTab.focus) activeTab.focus({ preventScroll: true });
      return;
    }

    if (currentKey === 'UP') {
      e.preventDefault();
      e.stopPropagation();
      if (atTop) {
        const { activeTabId } = this.props;
        const isPlayersTab = activeTabId === '#match-detail-tab-players';
        const focusedElement = document && document.activeElement ? document.activeElement : null;
        const focusedId = focusedElement && focusedElement.id ? focusedElement.id : '';
        const isTeamSwitchFocused = focusedId.indexOf('match-detail-team-') === 0;

        if (isPlayersTab) {
          if (window.SpatialNavigation && typeof window.SpatialNavigation.makeFocusable === 'function') {
            window.SpatialNavigation.makeFocusable();
          }

          if (isTeamSwitchFocused) {
            const playersTabSelector = '#match-detail-tab-players';
            const playersTabTarget = document.querySelector(playersTabSelector);
            if (playersTabTarget) {
              if (window.SpatialNavigation && typeof window.SpatialNavigation.focus === 'function') {
                window.SpatialNavigation.focus(playersTabSelector);
              } else if (playersTabTarget.focus) {
                playersTabTarget.focus({ preventScroll: true });
              }
            }
            return;
          }

          const teamTargetSelector = '#match-detail-team-arg';
          const teamTarget = document.querySelector(teamTargetSelector);
          if (teamTarget) {
            if (window.SpatialNavigation && typeof window.SpatialNavigation.makeFocusable === 'function') {
              window.SpatialNavigation.makeFocusable();
            }
            if (window.SpatialNavigation && typeof window.SpatialNavigation.focus === 'function') {
              window.SpatialNavigation.focus(teamTargetSelector);
            } else if (teamTarget.focus) {
              teamTarget.focus({ preventScroll: true });
            }
          }
          return;
        }

        const target = activeTabId ? document.querySelector(activeTabId) : null;
        if (target && target.focus) target.focus({ preventScroll: true });
        return;
      }
      el.scrollTop = el.scrollTop - 120;
      this.setThumbExpandedByScroll(true);
      this.updateThumb();
      return;
    }

    // DOWN con overflow no está en el fondo — captureKeyDown ya lo maneja;
    // este bloque es fallback para navegadores sin capture o cuando captureKeyDown no aplica.
    if (currentKey === 'DOWN') {
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop = el.scrollTop + 120;
      this.setThumbExpandedByScroll(true);
      this.updateThumb();
    }
  };

  handleFocus = () => {
    // Mantener el estado visual según si ya hubo interacción de scroll.
    this.setScrollThumbFocused(this.isThumbExpandedByScroll);

    // En Chromium antiguo (Smart TV) el browser hace scroll-into-view al recibir el foco,
    // desplazando el layout. Bloqueamos overflow en html/body antes del ciclo de pintura
    // y restauramos en doble-RAF para anular el scroll automático del browser.
    var htmlEl = document.documentElement;
    var bodyEl_old = document.body;
    var prevHtmlOverflow = htmlEl ? htmlEl.style.overflow : '';
    var prevBodyOverflow = bodyEl_old ? bodyEl_old.style.overflow : '';
    var prevHtmlScrollTop = htmlEl ? htmlEl.scrollTop : 0;
    var prevBodyScrollTop = bodyEl_old ? bodyEl_old.scrollTop : 0;
    var prevWinTop = window.pageYOffset || prevHtmlScrollTop || 0;

    if (htmlEl) htmlEl.style.overflow = 'hidden';
    if (bodyEl_old) bodyEl_old.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (htmlEl) {
          htmlEl.style.overflow = prevHtmlOverflow;
          htmlEl.scrollTop = prevHtmlScrollTop;
        }
        if (bodyEl_old) {
          bodyEl_old.style.overflow = prevBodyOverflow;
          bodyEl_old.scrollTop = prevBodyScrollTop;
        }
        window.scrollTo(0, prevWinTop);
      });
    });
  };

  handleBlur = () => {
    this.clearPlayersSwitchScrollStyle();
    this.syncSummaryToggleScrollStyle();

    const el = this.contentRef;
    if (!el) return;
    const hasScroll = el.scrollHeight > el.clientHeight + 1;
    if (!hasScroll) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    if (atBottom) {
      // SpatialNavigation robó el foco al llegar al fondo; recuperarlo para que UP funcione.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (el && el.focus) el.focus({ preventScroll: true });
        });
      });
    }
  };

  handleScroll = () => {
    this.setThumbExpandedByScroll(true);
    this.trackScrollMilestones();
    this.syncScrollDrivenButtonStyles();
    this.updateThumb();
  };

  updateThumb = () => {
    const content = this.contentRef;
    const rail = this.railRef;
    const thumb = this.thumbRef;
    if (!content || !rail || !thumb) return;

    const contentHeight = content.scrollHeight;
    const visibleHeight = content.clientHeight;
    const railHeight = rail.clientHeight;

    if (contentHeight <= 0 || visibleHeight <= 0 || railHeight <= 0) return;

    const thumbHeightPx = 108; // 10.8rem
    const maxTop = Math.max(railHeight - thumbHeightPx, 0);
    const scrollRatio = content.scrollTop / (contentHeight - visibleHeight || 1);
    const top = maxTop * scrollRatio;

    thumb.style.height = `${thumbHeightPx}px`;
    thumb.style.transform = `translateY(${top}px)`;
    thumb.style.opacity = contentHeight > visibleHeight ? '1' : '0';

    this.syncScrollDrivenButtonStyles();
  };

  render() {
    const { children, topContent, showCarousel, showUpsell, isSingleChannel } = this.props;
    const hasTopContent = !!topContent;

    var bodyHeight, innerPaddingBottom;
    if (showCarousel && isSingleChannel) {
      bodyHeight = '32rem';
      innerPaddingBottom = '20rem';
    } else if (showCarousel) {
      bodyHeight = '27rem';
      innerPaddingBottom = '20rem';
    } else if (showUpsell) {
      bodyHeight = '30.5rem';
      innerPaddingBottom = '14rem';
    } else {
      bodyHeight = '45rem';
      innerPaddingBottom = '4rem';
    }

    return (
      <div
        className={`match-detail-body${hasTopContent ? ' match-detail-body--with-top' : ''}`}
        style={{ height: bodyHeight }}
      >
        <span
          className="match-detail-body__scroll-rail"
          aria-hidden="true"
          ref={(el) => { this.railRef = el; }}
        >
          <span
            className="match-detail-body__scroll-thumb"
            ref={(el) => { this.thumbRef = el; }}
          />
        </span>
        <div
          className="match-detail-body__content focusable"
          tabIndex="0"
          ref={(el) => { this.contentRef = el; }}
          onKeyDown={this.handleKeyDown}
          onFocus={this.handleFocus}
          onScroll={this.handleScroll}
          onBlur={this.handleBlur}
        >
          {hasTopContent && (
            <div className="match-detail-body__top">
              {topContent}
            </div>
          )}
          <div className="match-detail-body__inner" style={{ paddingBottom: innerPaddingBottom }}>
            {children}
          </div>
        </div>
      </div>
    );
  }
}

MatchDetailBody.propTypes = {
  children: PropTypes.node,
  activeTabId: PropTypes.string,
  mainMenuLabel: PropTypes.string,
  parentTabLabel: PropTypes.string,
  childTabLabel: PropTypes.string,
  topContent: PropTypes.node,
  showCarousel: PropTypes.bool,
  showUpsell: PropTypes.bool,
  isSingleChannel: PropTypes.bool
};

MatchDetailBody.defaultProps = {
  children: null,
  activeTabId: '',
  mainMenuLabel: null,
  parentTabLabel: null,
  childTabLabel: null,
  topContent: null,
  showCarousel: false,
  showUpsell: false,
  isSingleChannel: false
};

export default MatchDetailBody;



// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailBody/MatchDetailBody.jsx